"use client";

import { useChatId } from "@ai-sdk-tools/store";
import type { ChatStatus } from "ai";
import { type RefObject, useEffect, useState } from "react";
import {
  type CommandMetadata,
  type CommandSelection,
  PromptCommands,
  PromptCommandsTextarea,
  useCommandActions,
} from "@/components/ai-elements/prompt-commands";
import {
  PromptInput,
  PromptInputActionAddAttachments,
  PromptInputActionMenu,
  PromptInputActionMenuContent,
  PromptInputActionMenuTrigger,
  PromptInputAttachment,
  PromptInputAttachments,
  PromptInputBody,
  PromptInputButton,
  type PromptInputMessage,
  PromptInputSubmit,
  PromptInputToolbar,
  PromptInputTools,
} from "@/components/ai-elements/prompt-input";
import { VoiceInputButton } from "@/components/ai-elements/voice-input-button";
import { Volume2Icon, VolumeXIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { LiveWaveform } from "@/components/ui/live-waveform";
import { useChatInterface } from "@/hooks/use-chat-interface";

export interface ChatInputMessage extends PromptInputMessage {
  metadata?: {
    toolChoice?: string;
  };
}

interface ChatInputProps {
  text: string;
  setText: (text: string) => void;
  textareaRef?: RefObject<HTMLTextAreaElement | null>;
  ttsAlignmentEnabled: boolean;
  setTtsAlignmentEnabled: (value: boolean) => void;
  onSubmit: (message: ChatInputMessage) => void;
  status?: ChatStatus;
  hasMessages: boolean;
  rateLimit?: {
    limit: number;
    remaining: number;
    reset: string;
    code?: string;
  } | null;
}

function ChatInputInner({
  text,
  setText,
  textareaRef,
  ttsAlignmentEnabled,
  setTtsAlignmentEnabled,
  onSubmit,
  status,
  hasMessages,
  rateLimit,
  selection,
}: ChatInputProps & {
  selection: CommandSelection;
}) {
  const { clearPills } = useCommandActions();
  const { setChatId } = useChatInterface();
  const chatId = useChatId();
  const [isRecording, setIsRecording] = useState(false);
  const [audioStream, setAudioStream] = useState<MediaStream | null>(null);

  const handleSubmit = (message: PromptInputMessage) => {
    if (chatId) {
      setChatId(chatId);
    }

    const trimmed = (message.text || "").trimStart();
    const slashMatch = trimmed.match(/^\/(\w+)/);
    const slashTool = slashMatch?.[1];
    const normalizedText = slashTool ? trimmed.slice(slashMatch[0].length).trimStart() : message.text;

    onSubmit({
      ...message,
      text: normalizedText,
      metadata: {
        toolChoice: (slashTool as string | undefined) || selection.toolChoice,
      },
    });

    clearPills();
  };

  return (
    <PromptInput
      globalDrop
      multiple
      onSubmit={handleSubmit}
      className="bg-white dark:bg-[#363636] border border-gray-200 dark:border-white/10 rounded-2xl shadow-[0_12px_32px_-8px_rgba(0,0,0,0.1)] dark:shadow-[0_12px_32px_-8px_rgba(0,0,0,0.3)] hover:shadow-[0_16px_40px_-8px_rgba(0,0,0,0.15)] transition-shadow duration-200 focus-within:outline-none focus-within:ring-2 focus-within:ring-[var(--mindup-pink)] focus-within:ring-offset-2 dark:focus-within:ring-offset-[var(--mindup-soft-black)]"
    >
      <PromptInputBody>
        {isRecording && audioStream ? (
          <div className="flex items-center justify-center w-full h-[56px] px-6">
            <LiveWaveform
              audioStream={audioStream}
              barCount={120}
              minHeight={12}
              maxHeight={32}
              className="w-full"
            />
          </div>
        ) : (
          <>
            <PromptInputAttachments>
              {(attachment) => <PromptInputAttachment data={attachment} />}
            </PromptInputAttachments>
            <PromptCommandsTextarea
              onChange={(event) => setText(event.target.value)}
              ref={textareaRef}
              value={text}
              placeholder={
                rateLimit?.code === "RATE_LIMIT_EXCEEDED"
                  ? "Rate limit exceeded. Please try again tomorrow."
                  : hasMessages
                    ? "Ask me anything about MindUP, or use a prompt above."
                    : "Now, use the prompts below or ask me anything you want to know about MindUP to get started."
              }
              disabled={rateLimit?.code === "RATE_LIMIT_EXCEEDED"}
              autoFocus
            />
          </>
        )}
      </PromptInputBody>

      <PromptInputToolbar>
        <PromptInputTools>
          <PromptInputActionMenu>
            <PromptInputActionMenuTrigger
              className={cn(
                // Base color and border - using MindUp brand colors
                "bg-[var(--mindup-off-white)] dark:bg-[#3a3a3a] border border-gray-200 dark:border-white/10 text-[var(--mindup-soft-black)] dark:text-white transition-colors outline-none",
                // Hover
                "hover:bg-[var(--mindup-pink-light)] hover:border-[var(--mindup-pink)]/30",
                // Focus
                "focus:bg-[var(--mindup-pink-light)] focus:border-[var(--mindup-pink)]/40 focus:ring-2 focus:ring-[var(--mindup-pink)]/30 focus:ring-offset-1",
                // Active
                "active:bg-[var(--mindup-pink)]/20 active:border-[var(--mindup-pink)]/50"
              )}
            />
            <PromptInputActionMenuContent>
              <PromptInputActionAddAttachments />
            </PromptInputActionMenuContent>
          </PromptInputActionMenu>
          <VoiceInputButton
            onTranscriptionChange={setText}
            onRecordingStateChange={setIsRecording}
            onAudioStreamChange={setAudioStream}
            textareaRef={textareaRef}
            className={cn(
              // Base
              "bg-[var(--mindup-off-white)] dark:bg-[#3a3a3a] border border-gray-200 dark:border-white/10 text-[var(--mindup-soft-black)] dark:text-white outline-none",
              "transition-colors transition-transform duration-200 ease-out",
              // Hover
              "hover:bg-[var(--mindup-mint)] hover:border-[var(--mindup-green)]/30 hover:-translate-y-0.5 hover:shadow-[0_8px_18px_rgba(125,219,191,0.15)]",
              // Focus
              "focus:bg-[var(--mindup-mint)] focus:border-[var(--mindup-green)]/40 focus:ring-2 focus:ring-[var(--mindup-green)]/30 focus:ring-offset-1",
              // Active
              "active:bg-[var(--mindup-green)]/30 active:border-[var(--mindup-green)]/50 active:translate-y-0 active:scale-[0.98]",
              // Recording state
              isRecording &&
                "bg-[var(--mindup-green)] border-[var(--mindup-green)] text-white shadow-[0_10px_22px_rgba(125,219,191,0.35)] animate-pulse"
            )}
          />
          <Button
            variant="outline"
            size="icon"
            onClick={() => setTtsAlignmentEnabled(!ttsAlignmentEnabled)}
            aria-pressed={ttsAlignmentEnabled}
            className={cn(
              "outline-none transition-colors transition-transform duration-200 ease-out",
              "bg-[var(--mindup-off-white)] dark:bg-[#3a3a3a] border border-gray-200 dark:border-white/10",
              "hover:bg-[var(--mindup-blue-light)] hover:border-[var(--mindup-blue)]/30 hover:-translate-y-0.5 hover:shadow-[0_8px_18px_rgba(100,149,255,0.15)]",
              "focus:bg-[var(--mindup-blue-light)] focus:border-[var(--mindup-blue)]/40 focus:ring-2 focus:ring-[var(--mindup-blue)]/30 focus:ring-offset-1",
              "active:bg-[var(--mindup-blue)]/30 active:border-[var(--mindup-navy)]/50 active:translate-y-0 active:scale-[0.98]",
              ttsAlignmentEnabled
                ? "text-white border-[var(--mindup-blue)] bg-[var(--mindup-blue)] shadow-[0_10px_22px_rgba(100,149,255,0.35)]"
                : "text-[var(--mindup-soft-black)] dark:text-white"
            )}
            title={
              ttsAlignmentEnabled
                ? "Speech sync enabled - text appears with voice"
                : "Speech sync disabled - text streams normally"
            }
          >
            {ttsAlignmentEnabled ? (
              <Volume2Icon className="size-4" />
            ) : (
              <VolumeXIcon className="size-4" />
            )}
          </Button>
        </PromptInputTools>
        <PromptInputSubmit
          disabled={
            (!text.trim() && !status) ||
            status === "streaming" ||
            rateLimit?.code === "RATE_LIMIT_EXCEEDED"
          }
          status={status}
        />
      </PromptInputToolbar>
    </PromptInput>
  );
}

export function ChatInput({
  text,
  setText,
  textareaRef,
  ttsAlignmentEnabled,
  setTtsAlignmentEnabled,
  onSubmit,
  status,
  hasMessages,
  rateLimit,
}: ChatInputProps) {
  const [metadata, setMetadata] = useState<CommandMetadata>({
    agents: [],
    tools: [],
  });
  const [selection, setSelection] = useState<CommandSelection>({});

  // Fetch metadata on mount
  useEffect(() => {
    fetch("/api/metadata")
      .then((res) => res.json())
      .then((data) => setMetadata(data))
      .catch((err) => console.error("Failed to fetch metadata:", err));
  }, []);

  return (
    <div>
      <PromptCommands metadata={metadata} onSelectionChange={setSelection}>
        <ChatInputInner
          text={text}
          setText={setText}
          textareaRef={textareaRef}
          ttsAlignmentEnabled={ttsAlignmentEnabled}
          setTtsAlignmentEnabled={setTtsAlignmentEnabled}
          onSubmit={onSubmit}
          status={status}
          hasMessages={hasMessages}
          rateLimit={rateLimit}
          selection={selection}
        />
      </PromptCommands>

      <div className="h-5">
        {rateLimit && rateLimit.remaining < 5 && (
          <div
            className={`py-2 text-[11px] border-t border-gray-200 dark:border-white/10 ${
              rateLimit.code === "RATE_LIMIT_EXCEEDED"
                ? "text-red-500"
                : "text-gray-500 dark:text-gray-400"
            }`}
          >
            <div className="flex w-full">
              <span>
                {rateLimit.code === "RATE_LIMIT_EXCEEDED"
                  ? "Rate limit exceeded - try again tomorrow"
                  : `Messages remaining: ${rateLimit.remaining} / ${rateLimit.limit}`}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
